/**
 * @module actions/Playback
 */

import * as Constants from '../constants';
import { AudioPlayerKeys } from '../constants/AudioPlayer';
import * as Utils from '../utils';

/**
 * action notifying of the intent to thumb up a track
 *
 * @param  {string} trackToken - trackToken
 * @returns {object} - return an Action of type TRACK_THUMBUP
 */
export function thumbUpTrack(trackToken) {
  return {
    type: Constants.Playback.ActionTypes.TRACK_THUMBUP,
    data: {
      trackToken,
    },
  };
}

/**
 * action notifying of the intent to thumb up a track
 *
 * @param  {string} trackToken - trackToken
 * @param  {boolean} skip - whether or not to skip track
 * @returns {object} - return an Action of type TRACK_THUMBDOWN
 */
export function thumbDownTrack(trackToken, skip = true) {
  return {
    type: Constants.Playback.ActionTypes.TRACK_THUMBDOWN,
    data: {
      trackToken,
      skip,
    },
  };
}

/**
 * [skipTrack description]
 *
 * @param  {[type]} stationId [description]
 * @returns {[type]}           [description]
 */
export function registerSkipTrack(stationId) {
  return {
    type: Constants.Playback.ActionTypes.REGISTER_SKIP_TRACK,
    data: {
      stationId,
      skip: Utils.Time.getNow(),
    },
  };
}

/**
 * action notifying of the intent to remove a thumb up feedback on a track
 *
 * @param  {string} trackToken - trackToken
 * @returns {object} - return an Action of type TRACK_REMOVE_THUMBUP
 */
export function unThumbUpTrack({ trackToken, feedbackId }) {
  return {
    type: Constants.Playback.ActionTypes.TRACK_REMOVE_THUMBUP,
    data: {
      trackToken,
      feedbackId,
    },
  };
}

/**
 * action notifying of the intent to remove a thumb down feedback on a track
 *
 * @param  {string} trackToken - trackToken
 * @returns {object} - return an Action of type TRACK_REMOVE_THUMBDOWN
 */
export function unThumbDownTrack({ trackToken, feedbackId }) {
  return {
    type: Constants.Playback.ActionTypes.TRACK_REMOVE_THUMBDOWN,
    data: {
      trackToken,
      feedbackId,
    },
  };
}

/**
 * action notifying of the intent to tire of track
 *
 * @param {string} stationId - stationId
 * @param {string} trackToken - trackToken
 * @returns {object} - return an Action of type TIRE_OF_TRACK
 */
export function tireOfTrack(stationId, trackToken) {
  return {
    type: Constants.Playback.ActionTypes.TIRE_OF_TRACK,
    data: {
      stationId,
      trackToken,
    },
  };
}

/**
 * Begins the flow of removing a station from the user's station list. Considered a playback action because
 * if a user tries to delete a station they are currently listening to, we need to switch stations, or halt playback
 * if they have no other stations
 *
 * @param  {string} stationId - the ID of the station that the user has elected to delete
 * @param  {string} componentName - the component from which the action originated
 * @returns {object} The action.
 */
export function startRemoveStationFlow(stationId, componentName) {
  return {
    type: Constants.Playback.ActionTypes.START_REMOVE_STATION_FLOW,
    data: {
      stationId,
      componentName,
    },
  };
}

/**
 * Removes a station from the user's station list in state. Does not perform API call to remove from server.
 *
 * @param  {string} stationId - the ID of the station that the user has elected to delete
 * @returns {object} The action.
 */
export function removeStation(stationId) {
  return {
    type: Constants.Playback.ActionTypes.REMOVE_STATION,
    data: {
      stationId,
    },
  };
}

/**
 * Restores a removed station from the user's station list in state. Does not perform API call to restore on server.
 *
 * @param  {string} stationId - the ID of the station that the user has elected to delete
 * @returns {object} The action.
 */
export function undoRemoveStation(stationId) {
  return {
    type: Constants.Playback.ActionTypes.UNDO_REMOVE_STATION,
    data: {
      stationId,
    },
  };
}

export function confirmRemoveStation(stationId) {
  return {
    type: Constants.Playback.ActionTypes.CONFIRM_REMOVE_STATION,
    data: {
      stationId,
    },
  };
}

/**
 * Stores, in state, the station set to be removed from the server with the next removeStation API call.
 *
 * @param  {string} stationId - the ID of the station that the user has elected to delete
 * @returns {object} The action.
 */
export function setStationToBeRemovedFromServer(stationId) {
  return {
    type: Constants.Playback.ActionTypes.SET_STATION_TO_BE_REMOVED_FROM_SERVER,
    data: {
      stationId,
    },
  };
}

export function clearStationToBeRemovedFromServer() {
  return {
    type: Constants.Playback.ActionTypes.CLEAR_STATION_TO_BE_REMOVED_FROM_SERVER,
  };
}

export function setIsInShuffleMode(toShuffle) {
  return {
    type: Constants.Playback.ActionTypes.SET_IS_IN_STATION_SHUFFLE_MODE,
    data: {
      toShuffle,
    },
  };
}

export function toggleShuffleAllStationsMode(shuffleAll) {
  return {
    type: Constants.Playback.ActionTypes.TOGGLE_SHUFFLE_ALL_STATIONS_MODE,
    data: {
      shuffleAll,
    },
  };
}

// Removes a station from the list of stations that comprise the shuffle station
export function removeStationFromShuffleList(stationToRemove) {
  return {
    type: Constants.Playback.ActionTypes.REMOVE_STATION_FROM_SHUFFLE_LIST,
    data: {
      stationToRemove,
    },
  };
}

// Adds station to the list of stations that comprise the shuffle station
export function addStationToShuffleList(stationToAdd) {
  return {
    type: Constants.Playback.ActionTypes.ADD_STATION_TO_SHUFFLE_LIST,
    data: {
      stationToAdd,
    },
  };
}

// Action to indicate that a handler should add all shuffleable stations to the shuffle list
export function addAllEligibleStationsToShuffle() {
  return {
    type: Constants.Playback.ActionTypes.ADD_ALL_ELIGIBLE_STATIONS_TO_SHUFFLE,
  };
}

export function restorePreviousShuffle() {
  return {
    type: Constants.Playback.ActionTypes.RESTORE_PREVIOUS_SHUFFLE_CONFIG,
  };
}

export function reloadAudioPlaybackLibrary(tryForceFlash = false) {
  return {
    type: Constants.Playback.ActionTypes.RELOAD_AUDIOPLAYBACK,
    data: {
      tryForceFlash,
    },
  };
}

export function setStationReceivedFragment(stationId) {
  return {
    type: Constants.Playback.ActionTypes.SET_STATION_RECEIVED_FRAGMENT,
    data: {
      stationId,
    },
  };
}

export function clickedPlayButton() {
  return {
    type: Constants.Playback.ActionTypes.CLICKED_PLAY,
  };
}

export function clickedPauseButton() {
  return {
    type: Constants.Playback.ActionTypes.CLICKED_PAUSE,
  };
}

export function clickedVideoPlayButton() {
  return {
    type: Constants.Playback.ActionTypes.CLICKED_VIDEO_PLAY,
  };
}

export function clickedVideoPauseButton() {
  return {
    type: Constants.Playback.ActionTypes.CLICKED_VIDEO_PAUSE,
  };
}

export function resetPlaylistIndex({ id, index = 0, duration = 0 }) {
  return {
    type: Constants.Playback.ActionTypes.RESET_PLAYLIST_INDEX,
    data: {
      id,
      index,
      duration,
    },
  };
}

export function setPlaylist({
  id = null,
  type = null,
  tracks = null,
  index = -1,
  trackAtIndex = null,
  trackModel = null,
} = {}) {
  const action = {
    type: Constants.Playback.ActionTypes.SET_PLAYLIST,
    data: {
      id,
      playlistType: type,
      index,
      trackAtIndex,
      trackModel,
    },
  };
  if (tracks) {
    action.data[AudioPlayerKeys.PLAYLIST] = tracks;
  }
  return action;
}

export function logSkipDRM(track, reason) {
  return {
    type: Constants.Playback.ActionTypes.LOG_SKIP_FOR_DRM,
    data: {
      track,
      reason,
    },
  };
}

export function registerArtistMessageMetric(track, metric) {
  return {
    type: Constants.Playback.ActionTypes.REGISTER_ARTIST_MESSAGE_METRIC,
    data: {
      track,
      metric,
    },
  };
}

export function registerVoiceTrackMetric(stationId, track, statType) {
  return {
    type: Constants.Playback.ActionTypes.REGISTER_VOICE_TRACK_METRIC,
    data: {
      stationId,
      track,
      statType,
    },
  };
}

export function registerReplayTrack() {
  return {
    type: Constants.Playback.ActionTypes.REPLAY_TRACK_SUCCESS,
    data: {
      date: Utils.DateUtils.getDate(),
    },
  };
}

export function playNextTrack({
  sourceId,
  sourceType,
  nextTrackForceNewPlaylist = false,
  logPlaybackActionsAnalytics = false,
  userInitiatedSkip = false,
}) {
  return {
    type: Constants.Playback.ActionTypes.PLAY_NEXT_TRACK,
    data: {
      sourceId,
      sourceType,
      nextTrackForceNewPlaylist,
      logPlaybackActionsAnalytics,
      userInitiatedSkip,
    },
  };
}

export function intentPlayTrack({
  playlistId,
  track,
  seekToInSeconds = null, // for stations
  seekTime = null, // for ondemand
  logPlaybackActionsAnalytics = true,
  playbackActionsAnalyticsUserInitiated = true,
  dontDispatchPlaybackResumed = false,
  userTimedOut = false,
  starting,
} = {}) {
  return {
    type: Constants.Playback.ActionTypes.INTENT_PLAY_TRACK,
    data: {
      playlistId,
      id: playlistId,
      track,
      seekToInSeconds,
      seekTime,
      logPlaybackActionsAnalytics,
      playbackActionsAnalyticsUserInitiated,
      dontDispatchPlaybackResumed,
      userTimedOut,
      starting,
    },
  };
}

export function removeArtistAudioMessages(artistAudioMessageSet) {
  return {
    type: Constants.Playback.ActionTypes.REMOVE_ARTIST_AUDIO_MESSAGES_FROM_PLAYLIST,
    data: {
      artistAudioMessageSet,
    },
  };
}

export function clickedArtistAudioMessage(track) {
  return {
    type: Constants.Playback.ActionTypes.CLICKED_ARTIST_AUDIO_MESSAGE,
    data: {
      track,
    },
  };
}

export function clickedVoiceTrack(stationId, track) {
  return {
    type: Constants.Playback.ActionTypes.CLICKED_VOICE_TRACK,
    data: {
      stationId,
      track,
    },
  };
}

export function triggeredArtistAudioMessageTile(track) {
  return {
    type: Constants.Playback.ActionTypes.TRIGGERED_ARTIST_AUDIO_MESSAGE_TILE,
    data: {
      track,
    },
  };
}

export function triggeredVoiceTrackTile(stationId, track) {
  return {
    type: Constants.Playback.ActionTypes.TRIGGERED_VOICE_TRACK_TILE,
    data: {
      stationId,
      track,
    },
  };
}

export function logTrackStarted({ trackToken, originalStationId }) {
  return {
    type: Constants.Playback.ActionTypes.PLAYBACK_LOG_TRACK_STARTED,
    data: originalStationId ? { trackToken, originalStationId } : { trackToken },
  };
}

export function logTrackResumed(forceActive = false) {
  return {
    type: Constants.Playback.ActionTypes.PLAYBACK_LOG_TRACK_RESUMED,
    data: {
      forceActive,
    },
  };
}
